(() => {
  if (customElements.get('hm-search-types')) return;

  const MAX_OPTIONS = 30;
  const requests = new Map();
  const normalize = value => value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase();

  function loadTypes(url) {
    if (!requests.has(url)) {
      const request = fetch(url, { credentials: 'same-origin' })
        .then(response => {
          if (!response.ok) throw new Error('Product types request failed');
          return response.text();
        })
        .then(html => {
          const source = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-hm-search-types]');
          const values = source && JSON.parse(source.textContent);
          if (!Array.isArray(values)) throw new Error('Invalid product types response');
          return [...new Set(values.filter(value => typeof value === 'string' && value.trim()))]
            .map(value => ({ value, search: normalize(value) }));
        })
        .catch(error => {
          requests.delete(url);
          throw error;
        });
      requests.set(url, request);
    }
    return requests.get(url);
  }

  class HMSearchTypes extends HTMLElement {
    connectedCallback() {
      if (this.hasAttribute('data-ready')) return;
      this.select = this.querySelector('.js-product-types');
      this.details = this.querySelector('details');
      this.toggle = this.querySelector('summary');
      this.query = this.querySelector('[role="combobox"]');
      this.list = this.querySelector('[data-type-list]');
      this.status = this.querySelector('[data-type-status]');
      this.label = this.querySelector('[data-type-label]');
      if (!this.select || !this.details || !this.query || !this.list) return;

      this.allLabel = this.select.options[0].textContent;
      this.options = [];
      this.activeIndex = -1;
      this.url = new URL(this.dataset.typesUrl || window.Shopify?.routes?.root || '/', location.origin);
      this.url.searchParams.set('section_id', 'hm-search-types');
      const previewId = new URLSearchParams(location.search).get('preview_theme_id');
      if (previewId) this.url.searchParams.set('preview_theme_id', previewId);

      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.details.addEventListener('toggle', () => this.onToggle(), { signal });
      this.query.addEventListener('input', event => {
        event.stopPropagation();
        this.renderOptions();
      }, { signal });
      this.addEventListener('keydown', event => this.onKeyDown(event), { signal });
      this.addEventListener('keyup', event => {
        if (this.details.open || this.consumedKey === event.key) event.stopPropagation();
        if (this.consumedKey === event.key) this.consumedKey = null;
      }, { signal });
      this.list.addEventListener('click', event => {
        const option = event.target.closest('[data-type-index]');
        if (option) this.choose(Number(option.dataset.typeIndex));
      }, { signal });
      this.select.addEventListener('change', () => this.updateLabel(), { signal });
      this.addEventListener('focusout', () => {
        setTimeout(() => {
          if (!this.contains(document.activeElement)) this.close(false);
        }, 0);
      }, { signal });
      document.addEventListener('pointerdown', event => {
        if (!this.contains(event.target)) this.close(false);
      }, { signal });

      this.updateLabel();
      this.details.hidden = false;
      this.setAttribute('data-ready', '');
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.removeAttribute('data-ready');
    }

    updateLabel() {
      this.label.textContent = this.select.selectedOptions[0]?.textContent || this.allLabel;
    }

    async onToggle() {
      const open = this.details.open;
      this.toggleAttribute('open', open);
      this.toggle.setAttribute('aria-expanded', String(open));
      this.query.setAttribute('aria-expanded', String(open));
      if (!open) {
        this.list.replaceChildren();
        this.options = [];
        this.setActive(-1);
        return;
      }

      this.loading = !this.types;
      this.failed = false;
      this.query.value = '';
      this.renderOptions();
      this.query.focus();
      if (!this.loading) return;
      try {
        this.types = await loadTypes(this.url.href);
      } catch (error) {
        this.failed = true;
      } finally {
        this.loading = false;
        if (this.isConnected && this.details.open) this.renderOptions(true);
      }
    }

    renderOptions(preserveActive = false) {
      const previousValue = preserveActive ? this.options[this.activeIndex]?.value : undefined;
      const term = normalize(this.query.value.trim());
      const matches = (this.types || []).filter(type => type.search.includes(term));
      const values = matches.slice(0, MAX_OPTIONS - 1).map(type => type.value);
      // Keep the existing selection visible when reopening a long list.
      if (!term && this.select.value && !values.includes(this.select.value)) {
        values.splice(0, 0, this.select.value);
        values.length = Math.min(values.length, MAX_OPTIONS - 1);
      }
      this.options = [{ value: '', label: this.allLabel }, ...values.map(value => ({ value, label: value }))];
      const fragment = document.createDocumentFragment();
      this.options.forEach((option, index) => {
        const item = document.createElement('div');
        item.className = 'hm-search-types__option';
        item.id = `${this.select.id}-option-${index}`;
        item.setAttribute('role', 'option');
        item.dataset.typeIndex = String(index);
        item.textContent = option.label;
        fragment.appendChild(item);
      });
      this.list.replaceChildren(fragment);
      this.list.setAttribute('aria-busy', String(Boolean(this.loading)));
      let activeIndex = -1;
      if (!this.loading && !this.failed && this.types) {
        activeIndex = term ? (values.length ? 1 : -1) : this.options.findIndex(option => option.value === this.select.value);
      }
      // Keep deliberate arrow navigation when the catalog arrives during a key sequence.
      if (previousValue !== undefined) activeIndex = this.options.findIndex(option => option.value === previousValue);
      this.setActive(activeIndex);

      if (this.loading) this.status.textContent = this.dataset.loadingLabel;
      else if (this.failed) this.status.textContent = this.dataset.errorLabel;
      else if (!matches.length && term) this.status.textContent = this.dataset.emptyLabel;
      else if (matches.length > MAX_OPTIONS - 1) this.status.textContent = this.dataset.moreLabel;
      else this.status.textContent = '';
    }

    setActive(index) {
      this.activeIndex = index;
      [...this.list.children].forEach((item, itemIndex) => item.setAttribute('aria-selected', String(itemIndex === index)));
      const active = this.list.children[index];
      if (active) {
        this.query.setAttribute('aria-activedescendant', active.id);
        active.scrollIntoView({ block: 'nearest' });
      } else this.query.removeAttribute('aria-activedescendant');
    }

    onKeyDown(event) {
      if (event.key === 'Tab') return;
      const toggling = event.target === this.toggle && (event.key === 'Enter' || event.key === ' ');
      if (!this.details.open && !toggling) return;
      event.stopPropagation();
      this.consumedKey = event.key;
      if (!this.details.open) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close(true);
      } else if (event.target === this.query) {
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          event.preventDefault();
          const direction = event.key === 'ArrowDown' ? 1 : -1;
          const next = this.activeIndex < 0 ? (direction === 1 ? 0 : this.options.length - 1) : (this.activeIndex + direction + this.options.length) % this.options.length;
          this.setActive(next);
        } else if (event.key === 'Enter') {
          event.preventDefault();
          this.choose(this.activeIndex);
        }
      }
    }

    choose(index) {
      const chosen = this.options[index];
      if (!chosen) return;
      const all = new Option(this.allLabel, '');
      this.select.replaceChildren(all);
      if (chosen.value) this.select.add(new Option(chosen.value, chosen.value));
      this.select.value = chosen.value;
      this.select.disabled = false;
      this.select.dispatchEvent(new Event('change', { bubbles: true }));
      this.close(true);
    }

    close(restoreFocus) {
      if (!this.details?.open) return;
      this.setActive(-1);
      this.details.open = false;
      if (restoreFocus) this.toggle.focus();
    }
  }

  customElements.define('hm-search-types', HMSearchTypes);
})();
